<template>
  <div class="relative bg-white dark:bg-[#0c1427] mb-6 rounded-3xl shadow-sm border border-slate-200/60 dark:border-slate-700/60 backdrop-blur-xl overflow-hidden transition-all duration-500 hover:shadow-lg hover:shadow-slate-200/40 dark:hover:shadow-slate-900/40">
    <!-- Subtle gradient overlay -->
    <div class="absolute inset-0 bg-gradient-to-br from-slate-50/40 to-transparent dark:from-slate-800/20 pointer-events-none"></div>

    <div class="relative p-6 md:p-8">
      <!-- Enhanced Header Section -->
      <div class="flex items-center justify-between mb-8">
        <!-- Modern Title with Icon -->
        <div class="flex items-center gap-4">
          <div class="relative">
            <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600 dark:from-purple-500 dark:via-indigo-500 dark:to-blue-600
                        flex items-center justify-center shadow-lg shadow-purple-200/50 dark:shadow-purple-900/30 transition-all duration-300 hover:scale-105">
              <svg class="w-6 h-6 filter drop-shadow-sm" fill="none" stroke="#ffffff" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <!-- Pulse effect -->
            <div class="absolute inset-0 w-12 h-12 rounded-2xl opacity-20 animate-pulse" style="background-color: #a855f7;"></div>
          </div>
          <div>
            <h3 class="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
              {{ directT('dashboard.crm.recentLeads') }}
            </h3>
            <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">{{ leads.length }} {{ directT('dashboard.crm.totalLeads') }}</p>
          </div>
        </div>

        <!-- Redesigned Time Filter Selector -->
        <div class="relative">
          <button
            type="button"
            @click.stop="toggleActive"
            class="inline-flex items-center gap-3 px-5 py-3 text-sm font-semibold
                   bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl
                   border border-slate-200/60 dark:border-slate-600/60
                   rounded-2xl hover:bg-white dark:hover:bg-slate-800
                   text-slate-700 dark:text-slate-200 transition-all duration-300
                   shadow-sm hover:shadow-lg hover:shadow-slate-200/40 dark:hover:shadow-slate-900/40
                   focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 dark:focus:border-purple-600"
            :class="{ 'ring-2 ring-purple-500/30 bg-white dark:bg-slate-800 shadow-lg': isActive }"
          >
            <span class="font-medium">{{ selectedTimeFilter }}</span>
            <svg class="w-4 h-4 transition-transform duration-300"
                 :class="{ 'rotate-180': isActive }"
                 fill="none" viewBox="0 0 24 24"
                 :stroke="isActive ? '#8b5cf6' : '#64748b'">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>

          <!-- Enhanced Dropdown Menu -->
          <div
            v-if="isActive"
            class="absolute top-full right-0 mt-3 w-56 bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl
                   border border-slate-200/60 dark:border-slate-600/60 rounded-2xl shadow-xl shadow-slate-200/40 dark:shadow-slate-900/40 z-20
                   transform transition-all duration-300 origin-top-right"
          >
            <div class="p-2">
              <button
                v-for="filter in timeFilters"
                :key="filter"
                type="button"
                @click="applyTimeFilter(filter)"
                class="w-full text-left px-4 py-3 text-sm font-medium rounded-xl
                       text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50
                       transition-all duration-200 flex items-center gap-3 group"
                :class="{
                  'bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 text-purple-700 dark:text-purple-300 shadow-sm': selectedTimeFilter === filter
                }"
              >
                <div class="w-2 h-2 rounded-full transition-all duration-200"
                     :style="{ backgroundColor: selectedTimeFilter === filter ? '#8b5cf6' : 'transparent' }"></div>
                <span>{{ filter }}</span>
                <svg v-if="selectedTimeFilter === filter" class="w-4 h-4 ml-auto" fill="#8b5cf6" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 1.414L9 10.586 7.707 9.293a1 1 0 011.414-1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="flex-1 min-h-0">
        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center p-12">
          <div class="flex flex-col items-center gap-6">
            <div class="relative">
              <div class="w-20 h-20 rounded-full animate-spin"
                   style="border: 4px solid #e2e8f0; border-top-color: #8b5cf6;"></div>
              <div class="absolute inset-0 w-20 h-20 rounded-full animate-spin animate-reverse"
                   style="border: 4px solid transparent; border-right-color: #6366f1;"></div>
            </div>
            <span class="text-sm font-semibold text-slate-600 dark:text-slate-400 animate-pulse">
              {{ directT('dashboard.crm.loadingData') }}
            </span>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="flex flex-col justify-center items-center p-12 text-center">
          <div class="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-red-50 to-red-100
                      dark:from-red-900/20 dark:to-red-800/20 flex items-center justify-center
                      border border-red-200/60 dark:border-red-800/60">
            <svg class="w-10 h-10" fill="none" stroke="#ef4444" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <div class="text-red-600 dark:text-red-400 font-bold text-lg mb-3">
            {{ directT('dashboard.crm.unableToLoadLeads') }}
          </div>
          <div class="text-slate-500 dark:text-slate-400">{{ error }}</div>
        </div>

        <!-- Empty State -->
        <div v-else-if="leads.length === 0" class="flex flex-col justify-center items-center p-12 text-center">
          <div class="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-slate-50 to-slate-100
                      dark:from-slate-800 dark:to-slate-700 flex items-center justify-center
                      border border-slate-200/60 dark:border-slate-600/60">
            <svg class="w-10 h-10" fill="none" stroke="#94a3b8" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
          </div>
          <div class="text-slate-700 dark:text-slate-300 font-bold text-lg mb-3">
            {{ directT('dashboard.crm.noLeadsFound') }}
          </div>
          <div class="text-slate-500 dark:text-slate-400">
            {{ directT('dashboard.crm.tryChangingFilter') }}
          </div>
        </div>

        <!-- Data Table -->
        <div v-else class="space-y-6">
          <!-- Enhanced Table Container -->
          <div class="relative overflow-hidden rounded-3xl bg-white/50 dark:bg-[#0c1427]/50 backdrop-blur-sm
                      border border-slate-200/60 dark:border-slate-600/60 shadow-lg">
            <div class="overflow-x-auto">
              <table class="w-full">
                <!-- Enhanced Table Header -->
                <thead>
                  <tr class="bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-800/50 dark:to-slate-700/50
                             border-b border-slate-200/60 dark:border-slate-600/60">
                    <th class="text-left px-6 py-5 text-sm font-bold text-slate-700 dark:text-slate-300
                               first:rounded-tl-3xl">
                      {{ directT('dashboard.customer') }}
                    </th>
                    <th class="text-left px-6 py-5 text-sm font-bold text-slate-700 dark:text-slate-300">
                      {{ directT('dashboard.crm.email') }}
                    </th>
                    <th class="text-left px-6 py-5 text-sm font-bold text-slate-700 dark:text-slate-300">
                      {{ directT('dashboard.crm.source') }}
                    </th>
                    <th class="text-left px-6 py-5 text-sm font-bold text-slate-700 dark:text-slate-300">
                      {{ directT('dashboard.status') }}
                    </th>
                    <th class="text-left px-6 py-5 text-sm font-bold text-slate-700 dark:text-slate-300
                               first:rounded-tl-2xl last:rounded-tr-2xl">
                      {{ directT('dashboard.crm.action') }}
                    </th>
                  </tr>
                </thead>

                <!-- Enhanced Table Body -->
                <tbody class="divide-y divide-slate-200/40 dark:divide-slate-600/40">
                  <tr v-for="item in paginatedItems" :key="item.id"
                      class="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-all duration-300 group">
                    <!-- Enhanced Customer Column -->
                    <td class="px-6 py-5">
                      <div class="flex items-center gap-4">
                        <div class="relative">
                          <div class="w-12 h-12 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200
                                      dark:from-slate-700 dark:to-slate-600 border-2 border-white dark:border-slate-800
                                      shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 transition-all duration-300
                                      group-hover:scale-105 group-hover:shadow-xl">
                            <img :src="getLeadAvatar(item)" class="w-full h-full object-cover" alt="user-image" />
                          </div>
                          <!-- Online indicator -->
                          <div class="absolute -bottom-0.5 -right-0.5 w-4 h-4 border-2 border-white dark:border-slate-800 rounded-full" style="background-color: #10b981;"></div>
                        </div>
                        <div>
                          <span class="font-bold text-slate-800 dark:text-slate-200 text-base">{{ item.name }}</span>
                          <div class="text-xs text-slate-500 dark:text-slate-400 mt-1">ID: {{ item.id }}</div>
                        </div>
                      </div>
                    </td>

                    <!-- Enhanced Email Column -->
                    <td class="px-6 py-5">
                      <div class="flex items-center gap-2">
                        <svg class="w-4 h-4" fill="none" stroke="#94a3b8" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"></path>
                        </svg>
                        <span class="text-slate-600 dark:text-slate-400 font-medium">{{ item.email }}</span>
                      </div>
                    </td>

                    <!-- Enhanced Source Column -->
                    <td class="px-6 py-5">
                      <div class="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold 
                                  bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300
                                  border border-slate-200/60 dark:border-slate-600/60">
                        <div class="w-2 h-2 rounded-full" style="background-color: #94a3b8;"></div>
                        {{ item.source }}
                      </div>
                    </td>

                    <!-- Revolutionary Status Column -->
                    <td class="px-6 py-5">
                      <div class="relative inline-flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-bold
                                  backdrop-blur-sm transition-all duration-300 hover:scale-105 cursor-pointer group/status
                                  border shadow-sm"
                           :style="getEnhancedStatusStyle(item.status)">
                        <!-- Status Icon -->
                        <div class="flex items-center justify-center w-5 h-5 rounded-full bg-white/30 backdrop-blur-sm">
                          <!-- New Status -->
                          <svg v-if="item.status?.toLowerCase() === 'new'" class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                          </svg>
                          <!-- In Progress / Qualified -->
                          <svg v-else-if="['in progress', 'qualified', 'close'].includes(item.status?.toLowerCase())" class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clip-rule="evenodd"></path>
                          </svg>
                          <!-- Pending -->
                          <svg v-else-if="item.status?.toLowerCase() === 'pending'" class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"></path>
                          </svg>
                          <!-- Rejected / Closed Lost -->
                          <svg v-else-if="['rejected', 'closed lost'].includes(item.status?.toLowerCase())" class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
                          </svg>
                          <!-- Closed Won -->
                          <svg v-else-if="item.status?.toLowerCase() === 'closed'" class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                          </svg>
                          <!-- Default -->
                          <div v-else class="w-2 h-2 rounded-full bg-current"></div>
                        </div>

                        <!-- Status Text -->
                        <span class="font-bold tracking-wide">{{ item.status }}</span>

                        <!-- Pulse Animation -->
                        <div class="absolute inset-0 rounded-2xl opacity-20 animate-pulse group-hover/status:animate-none"
                             :style="{ backgroundColor: getStatusBaseColor(item.status) }"></div>
                      </div>
                    </td>

                    <!-- Enhanced Actions Column -->
                    <td class="px-6 py-5">
                      <div class="flex items-center gap-3">
                        <button @click="editLead(item)"
                                class="w-10 h-10 rounded-2xl text-white
                                       hover:scale-110 transition-all duration-300
                                       flex items-center justify-center group shadow-lg
                                       hover:shadow-xl"
                                style="background: linear-gradient(135deg, #3b82f6, #2563eb); box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);"
                                @mouseover="$event.target.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.4)'"
                                @mouseout="$event.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)'">
                          <svg class="w-4 h-4 group-hover:scale-110 transition-transform filter drop-shadow-sm" fill="none" stroke="#ffffff" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                          </svg>
                        </button>
                        <button @click="deleteLead(item)"
                                class="w-10 h-10 rounded-2xl text-white
                                       hover:scale-110 transition-all duration-300
                                       flex items-center justify-center group shadow-lg
                                       hover:shadow-xl"
                                style="background: linear-gradient(135deg, #ef4444, #dc2626); box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);"
                                @mouseover="$event.target.style.boxShadow = '0 8px 25px rgba(239, 68, 68, 0.4)'"
                                @mouseout="$event.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)'">
                          <svg class="w-4 h-4 group-hover:scale-110 transition-transform filter drop-shadow-sm" fill="none" stroke="#ffffff" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Enhanced Pagination -->
          <div v-if="totalPages > 1" class="flex items-center justify-between">
            <div class="text-sm font-medium text-slate-600 dark:text-slate-400 bg-slate-100/50 dark:bg-slate-700/50
                        px-4 py-2 rounded-xl backdrop-blur-sm border border-slate-200/60 dark:border-slate-600/60">
              {{ directT('dashboard.crm.showingResultsFromTo').replace('{start}', String(startItem + 1)).replace('{end}', String(endItem)).replace('{total}', String(leads.length)) }}
            </div>

            <div class="flex items-center gap-2">
              <button
                @click="prevPage"
                :disabled="currentPage === 1"
                class="px-4 py-2.5 text-sm font-bold rounded-xl border transition-all duration-300
                       disabled:opacity-50 disabled:cursor-not-allowed
                       bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl border-slate-200/60 dark:border-slate-600/60
                       text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-800
                       hover:shadow-lg hover:shadow-slate-200/40 dark:hover:shadow-slate-900/40
                       disabled:hover:shadow-none"
              >
                קודם
              </button>

              <div class="flex items-center gap-1">
                <button
                  v-for="page in totalPages"
                  :key="page"
                  @click="goToPage(page)"
                  class="w-12 h-12 text-sm font-bold rounded-xl transition-all duration-300 relative overflow-hidden"
                  :class="{
                    'bg-gradient-to-br from-purple-500 to-indigo-600 text-white shadow-lg shadow-purple-200/50 dark:shadow-purple-900/30 scale-110': page === currentPage,
                    'bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/60 text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-800 hover:shadow-lg': page !== currentPage
                  }"
                >
                  <span class="relative z-10">{{ page }}</span>
                  <div v-if="page === currentPage" class="absolute inset-0 bg-gradient-to-br from-purple-600 to-indigo-700 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              </div>

              <button
                @click="nextPage"
                :disabled="currentPage === totalPages"
                class="px-4 py-2.5 text-sm font-bold rounded-xl border transition-all duration-300
                       disabled:opacity-50 disabled:cursor-not-allowed
                       bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl border-slate-200/60 dark:border-slate-600/60
                       text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-800
                       hover:shadow-lg hover:shadow-slate-200/40 dark:hover:shadow-slate-900/40
                       disabled:hover:shadow-none"
              >
                הבא
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Revolutionary Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 flex items-center justify-center z-50 p-4">
      <div class="fixed inset-0 bg-black/20 backdrop-blur-md" @click="closeDeleteModal"></div>
      <div class="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl p-8 rounded-3xl shadow-2xl
                  w-full max-w-md z-10 relative transform transition-all border border-slate-200/60 dark:border-slate-700/60">
        <!-- Modal Header -->
        <div class="flex justify-between items-center mb-8">
          <div class="flex items-center gap-4">
            <div class="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
                 style="background: linear-gradient(135deg, #ef4444, #dc2626); box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);">
              <svg class="w-6 h-6" fill="none" stroke="#ffffff" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
              {{ directT('dashboard.crm.confirmDeletion') }}
            </h3>
          </div>
          <button
            @click="closeDeleteModal" 
            class="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-2 -m-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div class="mb-8">
          <p class="text-slate-700 dark:text-slate-300 text-sm">
            {{ directT('dashboard.crm.deleteConfirmation') }}
          </p>
        </div>
        
        <div class="flex justify-between items-center gap-4">
          <button
            type="button"
            @click="closeDeleteModal"
            class="flex-1 py-3 px-4 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 transition-all hover:bg-slate-200 dark:hover:bg-slate-600 rounded-2xl font-semibold"
          >
            {{ directT('dashboard.crm.cancel') }}
          </button>
          <button
            type="button"
            @click="confirmDelete"
            class="flex-1 py-3 px-4 text-white transition-all rounded-2xl shadow-lg font-semibold"
            style="background: linear-gradient(135deg, #ef4444, #dc2626); box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);"
          >
            {{ directT('dashboard.crm.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, onMounted } from "vue";
import { useClickOutside } from "@/utils/useClickOutside";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import { useRouter } from 'vue-router';
import { useI18n } from "vue-i18n";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// ייבוא מספר תמונות משתמשים קיימות במקום תמונת ברירת מחדל אחת
import user1 from "@/assets/images/users/user1.jpg";
import user2 from "@/assets/images/users/user2.jpg";
import user3 from "@/assets/images/users/user3.jpg";
import user4 from "@/assets/images/users/user4.jpg";
import user5 from "@/assets/images/users/user5.jpg";

// Create type for translation object structure
type TranslationObject = Record<string, any>;

interface Status {
  id: string;
  name: string;
  color: string;
}

interface Lead {
  id: string;
  name: string;
  email: string;
  source: string;
  status: string;
  created_at: string;
  phone: string;
  assigned_to?: string;
  assigned_user?: {
    email: string;
    display_name?: string;
    profile_picture_link?: string;
  };
  profileImageLink?: string;
}

export default defineComponent({
  name: "RecentLeads",
  setup() {
    const { isActive, toggleActive } = useClickOutside("dropdownToggle");
    const router = useRouter();
    const { locale } = useI18n();
    const statuses = ref<Status[]>([]);
    const organizationId = ref<string | null>(null);
    const loading = ref(false);
    const leads = ref<Lead[]>([]);
    const error = ref<string | null>(null);
    const showDeleteModal = ref(false);
    const leadToDelete = ref<Lead | null>(null);
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };
    
    // הגדרת סוגי פילטרי זמן זמינים
    const timeFilters = [
      directT('dashboard.thisDay'),
      directT('dashboard.thisWeek'),
      directT('dashboard.thisMonth'),
      directT('dashboard.thisYear')
    ];
    const selectedTimeFilter = ref(directT('dashboard.thisMonth'));

    // מערך של תמונות ברירת מחדל לשימוש כאווטארים
    const defaultAvatars = [user1, user2, user3, user4, user5];

    const itemsPerPage = 5;
    const currentPage = ref(1);

    // Get paginated items
    const paginatedItems = computed(() => {
      const startIndex = (currentPage.value - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return leads.value.slice(startIndex, endIndex);
    });

    // Calculate total pages
    const totalPages = computed(() => {
      return Math.ceil(leads.value.length / itemsPerPage);
    });

    // Calculate start and end items for pagination display
    const startItem = computed(() => {
      return (currentPage.value - 1) * itemsPerPage;
    });

    const endItem = computed(() => {
      const end = startItem.value + itemsPerPage;
      return end > leads.value.length ? leads.value.length : end;
    });

    // פונקציה להחלת פילטר זמן
    const applyTimeFilter = (filter: string) => {
      selectedTimeFilter.value = filter;
      toggleActive();
      currentPage.value = 1; // חזרה לעמוד הראשון
      fetchLeads(); // טעינה מחדש של הלידים עם הפילטר החדש
    };

    // חישוב מגבלת התאריכים לפי הפילטר הנוכחי
    const getDateFilter = () => {
      const now = new Date();
      let startDate;
      
      switch (selectedTimeFilter.value) {
        case "This Day":
          // היום הנוכחי (מתחילת היום)
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
          
        case "This Week": {
          // מתחילת השבוע הנוכחי (יום ראשון)
          const dayOfWeek = now.getDay(); // 0-6 (0 = יום ראשון)
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
          break;
        }
          
        case "This Month":
          // מתחילת החודש הנוכחי
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
          
        case "This Year":
          // מתחילת השנה הנוכחית
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
          
        default:
          // ברירת מחדל - יום אחד
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
      }
      
      // הפיכה לפורמט ISO לשימוש בשאילתה
      return startDate.toISOString();
    };



    // Fetch leads and status options when component mounts
    onMounted(async () => {
      await fetchStatusOptions();
      await fetchLeads();
    });

    // Fetch status options from database
    const fetchStatusOptions = async () => {
      try {
        organizationId.value = await getCurrentOrgId();
        
        if (!organizationId.value) {
          console.warn('Cannot fetch statuses: Missing organization ID');
          return;
        }
        
        // Fetch statuses
        const { data: statusData, error: statusError } = await supabase
          .from('statusleads')
          .select('*')
          .eq('organization_id', organizationId.value);
        
        if (statusError) throw statusError;
        
        statuses.value = (statusData || []).map((item: any) => ({
          id: item.id,
          name: item.status,
          color: item.color || '#605DFF'
        }));
      } catch (error) {
        console.error('Error fetching status options:', error);
      }
    };

    // Fetch leads from database based on current organization
    const fetchLeads = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        if (!organizationId.value) {
          organizationId.value = await getCurrentOrgId();
        }
        
        if (!organizationId.value) {
          throw new Error('Unable to determine organization ID');
        }
        
        // חישוב תאריך ההתחלה לפילטור
        const startDate = getDateFilter();
        
        // שאילתה עם פילטר תאריך
        const { data, error: leadsError } = await supabase
          .from('leads')
          .select(`
            id, 
            name, 
            email, 
            phone, 
            status, 
            source, 
            created_at,
            assigned_to,
            profileImageLink
          `)
          .eq('org_id', organizationId.value)
          .gte('created_at', startDate) // פילטור לפי תאריך
          .order('created_at', { ascending: false })
          .limit(20);
        
        if (leadsError) throw leadsError;
        
        if (data) {
          leads.value = data.map((lead: any) => ({
            id: lead.id,
            name: lead.name || 'Unknown',
            email: lead.email || '',
            phone: lead.phone || '',
            source: lead.source || 'Unknown',
            status: lead.status || 'New',
            created_at: lead.created_at,
            assigned_to: lead.assigned_to,
            profileImageLink: lead.profileImageLink
          }));
        }
      } catch (err: any) {
        error.value = err.message || 'Failed to fetch leads';
        console.error('Error fetching leads:', err);
      } finally {
        loading.value = false;
      }
    };

    // Get status class for styling
    const getStatusClass = (status: string) => {
      if (!status) {
        // Default styling for no status
        return {
          backgroundColor: 'rgba(16, 185, 129, 0.15)', // Light green background
          color: '#10B981', // Green text
        };
      }
      
      // First, check if we have this status in our statuses array
      const statusObj = statuses.value.find(s => s.name.toLowerCase() === status.toLowerCase());
      
      if (statusObj && statusObj.color) {
        // Use the status's color from the database
        const colorHex = statusObj.color;
        
        // Convert hex to RGB for generating lighter background colors
        const r = parseInt(colorHex.slice(1, 3), 16);
        const g = parseInt(colorHex.slice(3, 5), 16);
        const b = parseInt(colorHex.slice(5, 7), 16);
        
        // Create a light background color with 15% opacity for light mode
        const bgColor = `rgba(${r}, ${g}, ${b}, 0.15)`;
        
        // Return inline styles for both background and text color
        return {
          backgroundColor: bgColor,
          color: colorHex
        };
      }
      
      // Fallback to predefined colors based on status name
      switch (status.toLowerCase()) {
        case 'new':
        case 'confirmed':
          return {
            backgroundColor: 'rgba(96, 93, 255, 0.15)',
            color: '#605DFF'
          };
        case 'in progress':
        case 'qualified': 
        case 'close':
          return {
            backgroundColor: 'rgba(16, 185, 129, 0.15)',
            color: '#10B981'
          };
        case 'pending':
          return {
            backgroundColor: 'rgba(245, 158, 11, 0.15)',
            color: '#F59E0B'
          };
        case 'rejected':
        case 'closed lost':
          return {
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            color: '#EF4444'
          };
        case 'closed won':
          return {
            backgroundColor: 'rgba(14, 165, 233, 0.15)',
            color: '#0EA5E9'
          };
        default:
          return {
            backgroundColor: 'rgba(156, 163, 175, 0.15)',
            color: '#9CA3AF'
          };
      }
    };

    // Function to get avatar for a lead
    const getLeadAvatar = (lead: any) => {
      // בדיקה האם יש תמונת פרופיל בשדה profileImageLink
      if (lead.profileImageLink) {
        return lead.profileImageLink;
      }
      
      // אם אין, נבחר תמונה אקראית מהתמונות שייבאנו
      // נשתמש במזהה הליד כדי לבחור תמונה קבועה עבור כל ליד
      const avatarIndex = lead.id ? lead.id.charCodeAt(0) % defaultAvatars.length : 0;
      return defaultAvatars[avatarIndex];
    };

    // Actions for leads
    const viewLead = (lead: Lead) => {
      router.push({ 
        name: 'LeadDetailPage', 
        params: { id: lead.id },
        query: { returnTo: router.currentRoute.value.fullPath }
      });
    };

    const editLead = (lead: Lead) => {
      router.push({ 
        name: 'LeadDetailPage', 
        params: { id: lead.id },
        query: { returnTo: router.currentRoute.value.fullPath, editMode: 'true' }
      });
    };

    // Close delete modal
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      leadToDelete.value = null;
    };

    // Confirm delete
    const confirmDelete = async () => {
      if (!leadToDelete.value) return;
      
      try {
        const { error: deleteError } = await supabase
          .from('leads')
          .delete()
          .eq('id', leadToDelete.value!.id);
        
        if (deleteError) throw deleteError;
        
        // Remove from local list
        leads.value = leads.value.filter(lead => lead.id !== leadToDelete.value!.id);
        
      } catch (err: any) {
        console.error('Error deleting lead:', err);
        alert(`Failed to delete lead: ${err.message}`);
      } finally {
        closeDeleteModal();
      }
    };
    
    // Delete lead
    const deleteLead = (lead: Lead) => {
      leadToDelete.value = lead;
      showDeleteModal.value = true;
    };

    // Enhanced Status Style Function
    const getEnhancedStatusStyle = (status: string) => {
      const statusStyles: Record<string, string> = {
        'new': 'background: linear-gradient(135deg, #10b981, #059669); color: white; border-color: #10b981;',
        'in progress': 'background: linear-gradient(135deg, #3b82f6, #2563eb); color: white; border-color: #3b82f6;',
        'qualified': 'background: linear-gradient(135deg, #8b5cf6, #7c3aed); color: white; border-color: #8b5cf6;',
        'pending': 'background: linear-gradient(135deg, #f59e0b, #d97706); color: white; border-color: #f59e0b;',
        'rejected': 'background: linear-gradient(135deg, #ef4444, #dc2626); color: white; border-color: #ef4444;',
        'closed lost': 'background: linear-gradient(135deg, #ef4444, #dc2626); color: white; border-color: #ef4444;',
        'closed': 'background: linear-gradient(135deg, #06b6d4, #0891b2); color: white; border-color: #06b6d4;',
        'close': 'background: linear-gradient(135deg, #10b981, #059669); color: white; border-color: #10b981;'
      };
      
      return statusStyles[status?.toLowerCase()] || 'background: linear-gradient(135deg, #6b7280, #4b5563); color: white; border-color: #6b7280;';
    };

    // Get base color for status
    const getStatusBaseColor = (status: string) => {
      const statusColors: Record<string, string> = {
        'new': '#10b981',
        'in progress': '#3b82f6',
        'qualified': '#8b5cf6',
        'pending': '#f59e0b',
        'rejected': '#ef4444',
        'closed lost': '#ef4444',
        'closed': '#06b6d4',
        'close': '#10b981'
      };
      
      return statusColors[status?.toLowerCase()] || '#6b7280';
    };

    // Pagination functions
    const goToPage = (page: number) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--;
      }
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
      }
    };

    // Initialize component
    onMounted(async () => {
      try {
        organizationId.value = await getCurrentOrgId();
        await fetchStatusOptions();
        await fetchLeads();
      } catch (error) {
        console.error('Failed to initialize RecentLeads component:', error);
      }
    });

    return {
      isActive,
      toggleActive,
      getStatusClass,
      getEnhancedStatusStyle,
      getStatusBaseColor,
      loading,
      error,
      leads,
      paginatedItems,
      currentPage,
      totalPages,
      startItem,
      endItem,
      goToPage,
      prevPage,
      nextPage,
      getLeadAvatar,
      // Action functions
      viewLead,
      editLead,
      deleteLead,
      // Delete modal
      showDeleteModal,
      leadToDelete,
      closeDeleteModal,
      confirmDelete,
      // חשיפת הפילטרים
      timeFilters,
      selectedTimeFilter,
      applyTimeFilter,
      directT
    };
  },
});
</script>

<style lang="scss" scoped>
ol {
  li {
    &.active {
      a {
        background-color: #605dff;
        color: #fff;
        border-color: #605dff;
      }
    }
    
    &.disabled a {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}

.custom-tooltip {
  position: relative;
  
  &:hover:after {
    content: attr(data-text);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 12px;
    padding: 4px 8px;
    border-radius: 4px;
    white-space: nowrap;
    margin-bottom: 5px;
    z-index: 10;
  }
}
</style>
